/**
 * Nav v2 badge count loaders (Path-to-GO S8).
 *
 * The registry declares badgeKeys on nav items; the shell resolves them via
 * these tenant-scoped, head-count queries. Fail-soft: any query error returns
 * null so the shell omits the badge rather than inventing a number.
 *
 * Pattern copied from the working SIRS/MAR/tasks loaders (exact head count +
 * tenant filter + fail-soft). People cannot-work uses an actor-scoped count
 * reader, with the existing obligation urgency bag as a missing-RPC fallback.
 */

import { addDaysLocal, dayBounds, toDateStringLocal } from '@/lib/dates';
import { DEFAULT_TIMEZONE } from '@/lib/timezone';
import type { NavBadgeCounts, NavBadgeKey } from '@/lib/navigation/nav-v2';

/**
 * Minimal chainable surface used by the loaders.
 *
 * Supabase's PostgrestQueryBuilder is progressive (eq/in appear after select),
 * so the loader layer accepts a structural any-chain rather than a closed
 * interface — callers pass the real createClient() or a vitest mock.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type NavBadgeQueryBuilder = any;

export type NavBadgeClient = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  from: (table: string) => any;
  rpc?: (
    fn: string,
    args?: Record<string, unknown>,
  ) => PromiseLike<{ data: unknown; error: unknown; status?: number }>;
  /** Current session, used to bind actor-scoped badge caches to the live session. */
  auth?: {
    getSession: () => PromiseLike<{ data: { session: { user: { id: string } } | null } }>;
  };
};

/** The authenticated user of the client's live session, or null when unknown. */
async function sessionAuthUserId(client: NavBadgeClient): Promise<string | null> {
  if (!client.auth) return null;
  try {
    const { data } = await client.auth.getSession();
    return data.session?.user.id ?? null;
  } catch {
    return null;
  }
}

/** Open investigation statuses that still need SIRS attention. */
export const SIRS_OPEN_INVESTIGATION_STATUSES = [
  'REPORTED',
  'UNDER_INVESTIGATION',
  'ACTION_REQUIRED',
  'AWAITING_TRIAGE',
] as const;

/** Active task statuses counted for "due today" (mirrors fetchTaskStats dueToday). */
export const TASKS_DUE_TODAY_STATUSES = ['TODO', 'IN_PROGRESS'] as const;

/** MAR rows not yet signed off for a scheduled slot. */
export const MAR_UNSIGNED_STATUSES = ['SCHEDULED'] as const;

export type DayWindow = {
  /** Tenant-local calendar day YYYY-MM-DD. */
  today: string;
  /** Inclusive start of today in tenant tz (ISO with offset). */
  startIso: string;
  /** Exclusive end of today = start of tomorrow (ISO with offset). */
  endExclusiveIso: string;
};

/**
 * Half-open [start, end) day window in the tenant timezone.
 * Matches fetchTaskStats / MAR schedule date-boundary discipline.
 *
 * Pass `now` for deterministic tests / fixed-instant evaluation (DST boundaries).
 * When omitted, uses the wall-clock instant at call time.
 */
export function tenantDayWindow(timezone: string, now?: Date): DayWindow {
  const tz = timezone || DEFAULT_TIMEZONE;
  const today = toDateStringLocal(now ?? new Date(), tz);
  const tomorrow = addDaysLocal(today, 1, tz);
  const { start: startIso } = dayBounds(today, tz);
  const { start: endExclusiveIso } = dayBounds(tomorrow, tz);
  return { today, startIso, endExclusiveIso };
}

/**
 * Shell badge bag keyed to the tenant that produced the counts.
 * Render only via {@link selectNavBadgeCountsForTenant} so a slow prior-tenant
 * fetch can never paint under a new active tenant.
 */
export type TenantKeyedNavBadgeState = {
  tenantId: string | null;
  counts: NavBadgeCounts;
};

export const EMPTY_TENANT_KEYED_BADGE_STATE: TenantKeyedNavBadgeState = {
  tenantId: null,
  counts: {},
};

/** Counts safe to render for the active tenant (empty when keys mismatch). */
export function selectNavBadgeCountsForTenant(
  state: TenantKeyedNavBadgeState,
  activeTenantId: string | null | undefined,
): NavBadgeCounts {
  if (!activeTenantId || state.tenantId !== activeTenantId) return {};
  return state.counts;
}

async function headCount(
  builder: PromiseLike<{ count: number | null; error: unknown }> | NavBadgeQueryBuilder,
): Promise<number | null> {
  try {
    const result = await (builder as PromiseLike<{ count: number | null; error: unknown }>);
    if (result?.error) return null;
    return result?.count ?? 0;
  } catch {
    return null;
  }
}

/**
 * SIRS Priority 1 open (24h regulatory clock) — reportable P1 incidents not
 * yet commission-notified and still in an open investigation status.
 */
export async function countSirsOpen24h(
  client: NavBadgeClient,
  params: { tenantId: string },
): Promise<number | null> {
  if (!params.tenantId) return null;
  // commission_notified may be NULL on legacy rows — treat NULL as unnotified
  // (same regulatory meaning as false). Owner may optionally backfill NULLs to
  // false at apply-time; not required for correct badge counts.
  return headCount(
    client
      .from('incidents')
      .select('id', { count: 'exact', head: true })
      .eq('tenant_id', params.tenantId)
      .eq('is_reportable', true)
      .eq('sirs_effective_priority', 1)
      .or('commission_notified.eq.false,commission_notified.is.null')
      .in('investigation_status', SIRS_OPEN_INVESTIGATION_STATUSES),
  );
}

/**
 * MAR administrations still SCHEDULED for the tenant-local calendar day
 * (not yet signed as given/refused/withheld/etc).
 */
export async function countMarUnsignedToday(
  client: NavBadgeClient,
  params: { tenantId: string; timezone: string; now?: Date },
): Promise<number | null> {
  if (!params.tenantId) return null;
  const window = tenantDayWindow(params.timezone, params.now);
  return headCount(
    client
      .from('current_medication_administrations')
      .select('id', { count: 'exact', head: true })
      .eq('tenant_id', params.tenantId)
      .in('status', MAR_UNSIGNED_STATUSES)
      .gte('scheduled_time', window.startIso)
      .lt('scheduled_time', window.endExclusiveIso),
  );
}

/**
 * My-tasks due today for the signed-in worker.
 * Resolves auth.users.id → public.users.id via auth0_id bridge, then counts
 * assigned workflow_tasks in TODO/IN_PROGRESS with due_date in today's window.
 */
export async function countTasksDueToday(
  client: NavBadgeClient,
  params: { tenantId: string; authUserId: string; timezone: string; now?: Date },
): Promise<number | null> {
  if (!params.tenantId || !params.authUserId) return null;

  let assigneeId: string | null = null;
  try {
    const { data, error } = await client
      .from('users')
      .select('id')
      .eq('auth0_id', params.authUserId)
      .eq('tenant_id', params.tenantId)
      .maybeSingle();
    if (error || !data?.id) return null;
    assigneeId = data.id;
  } catch {
    return null;
  }

  const window = tenantDayWindow(params.timezone, params.now);
  return headCount(
    client
      .from('workflow_tasks')
      .select('id', { count: 'exact', head: true })
      .eq('tenant_id', params.tenantId)
      .eq('assigned_to_id', assigneeId)
      .in('status', TASKS_DUE_TODAY_STATUSES)
      .gte('due_date', window.startIso)
      .lt('due_date', window.endExclusiveIso),
  );
}

export type LoadNavBadgeCountsParams = {
  tenantId: string;
  /** auth.users.id from the session store — used for personal tasks and the actor-scoped People badge. */
  authUserId?: string | null;
  /** Canonical live role, used to keep the People badge cache actor-scoped. */
  role?: string | null;
  timezone?: string | null;
  /** Which keys to load; defaults to the three S8 keys. */
  keys?: readonly NavBadgeKey[];
};

const DEFAULT_S8_KEYS: readonly NavBadgeKey[] = [
  'sirs_open_24h',
  'mar_unsigned_today',
  'tasks_due_today',
];

/**
 * Batched loader for the three declared-but-unwired S8 badge keys.
 * Returns only keys that resolved (omit on fail so the shell shows no badge).
 */
export async function loadNavV2BadgeCounts(
  client: NavBadgeClient,
  params: LoadNavBadgeCountsParams,
): Promise<NavBadgeCounts> {
  const keys = params.keys ?? DEFAULT_S8_KEYS;
  const tz = params.timezone || DEFAULT_TIMEZONE;
  const out: NavBadgeCounts = {};

  const want = new Set(keys);
  const jobs: Array<Promise<void>> = [];

  if (want.has('sirs_open_24h')) {
    jobs.push(
      countSirsOpen24h(client, { tenantId: params.tenantId }).then((n) => {
        if (n !== null) out.sirs_open_24h = n;
      }),
    );
  }
  if (want.has('mar_unsigned_today')) {
    jobs.push(
      countMarUnsignedToday(client, {
        tenantId: params.tenantId,
        timezone: tz,
      }).then((n) => {
        if (n !== null) out.mar_unsigned_today = n;
      }),
    );
  }
  if (want.has('tasks_due_today') && params.authUserId) {
    jobs.push(
      countTasksDueToday(client, {
        tenantId: params.tenantId,
        authUserId: params.authUserId,
        timezone: tz,
      }).then((n) => {
        if (n !== null) out.tasks_due_today = n;
      }),
    );
  }

  if (want.has('people_cannot_work')) {
    jobs.push(
      countPeopleCannotWork(client, {
        tenantId: params.tenantId,
        authUserId: params.authUserId ?? null,
        role: params.role ?? null,
      }).then((n) => {
        if (n !== null) out.people_cannot_work = n;
      }),
    );
  }

  await Promise.all(jobs);
  return out;
}

const PEOPLE_BADGE_PREFIX = 'nav-badge:people-cannot-work:v2:';
export const PEOPLE_BADGE_INVALIDATED_EVENT = 'people-badge-invalidated';
const peopleBadgePrefix = (tenantId: string) => `${PEOPLE_BADGE_PREFIX}${tenantId}:`;
const peopleBadgeGenerations = new Map<string, number>();
const peopleBadgeFlights = new Map<
  string,
  { generation: number; promise: Promise<number | null> }
>();

/**
 * Discard this tab's in-flight People reads for the tenant, so a read started
 * before an invalidation can neither be joined nor written back to the cache.
 * Called directly for a same-tab save and from the storage event for another tab's.
 */
export function noteExternalPeopleBadgeInvalidation(tenantId: string): void {
  peopleBadgeGenerations.set(tenantId, (peopleBadgeGenerations.get(tenantId) ?? 0) + 1);
  const prefix = peopleBadgePrefix(tenantId);
  for (const key of peopleBadgeFlights.keys()) {
    if (key.startsWith(prefix)) peopleBadgeFlights.delete(key);
  }
}

/** Invalidate all cached actor/role counts for this tenant after a saved requirement. */
export function invalidatePeopleCannotWorkBadge(tenantId: string): void {
  noteExternalPeopleBadgeInvalidation(tenantId);
  const prefix = peopleBadgePrefix(tenantId);
  try {
    const keys = Array.from({ length: globalThis.localStorage.length }, (_, i) =>
      globalThis.localStorage.key(i),
    );
    for (const key of keys) {
      if (key?.startsWith(prefix)) globalThis.localStorage.removeItem(key);
    }
  } catch {
    // Storage can be denied; invalidation and saved work remain fail-soft.
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(PEOPLE_BADGE_INVALIDATED_EVENT, { detail: tenantId }));
  }
}

/** Whether another tab changed this tenant's People badge cache. */
export function isPeopleBadgeStorageKey(key: string | null, tenantId: string): boolean {
  return key === null || key.startsWith(peopleBadgePrefix(tenantId));
}

/**
 * Cannot-work count from the workforce obligation projection.
 * Uses the dedicated actor-scoped reader, with a page-size-1 legacy fallback
 * when it is missing. Denied or malformed reads remain unknown.
 */
export async function countPeopleCannotWork(
  client: NavBadgeClient,
  params: { tenantId: string; authUserId?: string | null; role?: string | null },
): Promise<number | null> {
  if (!params.tenantId || !params.authUserId || !params.role || typeof client.rpc !== 'function') {
    return null;
  }
  const rpc = client.rpc.bind(client);
  // Bind both storage and single-flight to the live auth actor, tenant and role.
  // A stale session-store actor must never read another actor's live count.
  const sessionMatches = async () => (await sessionAuthUserId(client)) === params.authUserId;
  if (!(await sessionMatches())) return null;
  const storageKey = `${peopleBadgePrefix(params.tenantId)}${params.authUserId}:${params.role}`;
  // Requirements changed elsewhere may remain stale for up to five minutes.
  // The People resolve drawer invalidates successful changes immediately.
  const cacheTtlMs = 5 * 60 * 1000;
  if (storageKey) {
    try {
      const cached = globalThis.localStorage.getItem(storageKey);
      if (cached) {
        const entry: unknown = JSON.parse(cached);
        if (
          entry &&
          typeof entry === 'object' &&
          'count' in entry &&
          typeof entry.count === 'number' &&
          Number.isInteger(entry.count) &&
          entry.count >= 0 &&
          'cachedAt' in entry &&
          typeof entry.cachedAt === 'number' &&
          Date.now() - entry.cachedAt >= 0 &&
          Date.now() - entry.cachedAt < cacheTtlMs
        ) {
          return entry.count;
        }
        // Leave an expired entry for the live write to overwrite: removing it
        // here would fire a storage event that makes every other tab refetch.
      }
    } catch {
      // Storage may be unavailable; the live request remains useful.
    }
  }

  const generation = peopleBadgeGenerations.get(params.tenantId) ?? 0;
  let flight = peopleBadgeFlights.get(storageKey);
  if (!flight) {
    flight = { generation, promise: readCount() };
    peopleBadgeFlights.set(storageKey, flight);
  }
  try {
    const count = await flight.promise;
    return (await sessionMatches()) &&
      flight.generation === (peopleBadgeGenerations.get(params.tenantId) ?? 0)
      ? count
      : null;
  } finally {
    if (peopleBadgeFlights.get(storageKey) === flight) peopleBadgeFlights.delete(storageKey);
  }

  async function readCount(): Promise<number | null> {
    try {
      const result = await rpc('count_worker_cannot_work_controlled');
      let count: number | null = null;
      if (!result.error && typeof result.data === 'number') {
        count = result.data;
      } else if (isMissingFunctionError(result.error)) {
        const legacy = await rpc('list_worker_obligations_controlled', {
          p_filters: { page: 1, page_size: 1, include_current: false },
        });
        const body = legacy.data;
        if (!legacy.error && body && typeof body === 'object' && 'urgency_counts' in body) {
          const urgencyCounts = body.urgency_counts;
          if (
            urgencyCounts &&
            typeof urgencyCounts === 'object' &&
            'cannot_work' in urgencyCounts
          ) {
            const parsed = Number(urgencyCounts.cannot_work ?? 0);
            if (Number.isFinite(parsed) && parsed >= 0) count = Math.floor(parsed);
          }
        }
      }
      if (count === null || !Number.isFinite(count) || count < 0) return null;
      // The session can change while the reads are in flight (sign-out / sign-in
      // as someone else). Then the count may belong to another actor: never cache
      // or return it for this one.
      if (
        !(await sessionMatches()) ||
        generation !== (peopleBadgeGenerations.get(params.tenantId) ?? 0)
      )
        return null;
      if (storageKey) {
        try {
          globalThis.localStorage.setItem(
            storageKey,
            JSON.stringify({ count: Math.floor(count), cachedAt: Date.now() }),
          );
        } catch {
          // Cache is an optimization and must not suppress a resolved badge.
        }
      }
      return Math.floor(count);
    } catch {
      return null;
    }
  }
}

function isMissingFunctionError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  // Only "function not found" (old database) falls back; auth and other
  // errors leave the badge unavailable.
  const code = 'code' in error ? error.code : undefined;
  return code === 'PGRST202';
}
